import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import './UserDetail.css'

type AssignedDrone = { id: string; drone_id: string; tail_number?: string; status: string }

type UserDetailData = {
  id: string
  username: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  is_active: boolean
  role_name?: string
  role_id?: string
  company_name?: string
  company_details?: string
  company_address?: string
  assigned_drones: AssignedDrone[]
}

type RoleInfo = { id: string; name: string; description?: string }
type DroneItem = { id: string; drone_id: string; tail_number?: string; status: string }

const ROLE_LABELS: Record<string, string> = {
  admin: 'מנהל', TenantAdmin: 'מנהל',
  customer: 'לקוח (משתמש רגיל)', pilot: 'טייס',
  technician: 'טכנאי', sales: 'מכירות',
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', phone: '',
    role_name: '', is_active: true, password: '',
    company_name: '', company_details: '', company_address: '',
  })
  const [saveError, setSaveError] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showAssignDrone, setShowAssignDrone] = useState(false)
  const [selectedDroneId, setSelectedDroneId] = useState('')

  const { data: user, isLoading, error } = useQuery<UserDetailData>({
    queryKey: ['user', id],
    queryFn: () => api.get(`/users/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const { data: roles = [] } = useQuery<RoleInfo[]>({
    queryKey: ['roles'],
    queryFn: () => api.get('/users/roles').then(r => r.data),
  })

  const { data: allDrones = [] } = useQuery<DroneItem[]>({
    queryKey: ['drones'],
    queryFn: () => api.get('/drones').then(r => r.data),
  })

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email ?? '',
        first_name: user.first_name ?? '',
        last_name: user.last_name ?? '',
        phone: user.phone ?? '',
        role_name: user.role_name ?? '',
        is_active: user.is_active,
        password: '',
        company_name: user.company_name ?? '',
        company_details: user.company_details ?? '',
        company_address: user.company_address ?? '',
      })
    }
  }, [user])

  const updateMutation = useMutation({
    mutationFn: (body: Partial<typeof form>) => api.put(`/users/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setSaveSuccess(true); setSaveError('')
      setTimeout(() => setSaveSuccess(false), 2500)
    },
    onError: (err: any) => { setSaveError(err.response?.data?.detail || 'שגיאה בשמירה'); setSaveSuccess(false) },
  })

  const assignDroneMutation = useMutation({
    mutationFn: (droneId: string) => api.post(`/users/${id}/drones/${droneId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      setShowAssignDrone(false); setSelectedDroneId('')
    },
  })

  const unassignDroneMutation = useMutation({
    mutationFn: (droneId: string) => api.delete(`/users/${id}/drones/${droneId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', id] }),
  })

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, unknown> = {
      email: form.email.trim(),
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      phone: form.phone.trim() || null,
      role_name: form.role_name || null,
      is_active: form.is_active,
      company_name: form.company_name.trim() || null,
      company_details: form.company_details.trim() || null,
      company_address: form.company_address.trim() || null,
    }
    if (form.password.trim()) payload.password = form.password.trim()
    updateMutation.mutate(payload as any)
  }

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm(f => ({ ...f, [key]: value }))

  // Drones not yet assigned to this user
  const assignedIds = new Set((user?.assigned_drones ?? []).map(d => d.id))
  const availableDrones = allDrones.filter(d => !assignedIds.has(d.id))

  if (!id) return <p className="page-error">מזהה חסר</p>
  if (isLoading) return <div className="page-loading">טוען...</div>
  if (error || !user) return (
    <div className="page-error">
      המשתמש לא נמצא. <Link to="/settings/user-management">חזרה לרשימה</Link>
    </div>
  )

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ')

  return (
    <div className="ud-page">
      <div className="ud-header">
        <Link to="/settings/user-management" className="ud-back">← ניהול משתמשים</Link>
        <h1>
          {fullName || user.username}
          {user.role_name && (
            <span className={`role-badge role-${user.role_name}`}>
              {ROLE_LABELS[user.role_name] ?? user.role_name}
            </span>
          )}
        </h1>
        <p className="ud-username" dir="ltr">@{user.username}</p>
      </div>

      <div className="ud-body">
        {/* ─── User Info ─── */}
        <section className="ud-section panel">
          <h2>פרטי משתמש</h2>
          <form onSubmit={handleSave} className="ud-form-grid">
            <label>
              אימייל
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required dir="ltr" />
            </label>
            <label>
              שם פרטי
              <input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="שם פרטי" />
            </label>
            <label>
              שם משפחה
              <input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="שם משפחה" />
            </label>
            <label>
              טלפון
              <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="טלפון" />
            </label>
            <label>
              תפקיד
              <select value={form.role_name} onChange={e => set('role_name', e.target.value)}>
                <option value="">— ללא תפקיד —</option>
                {roles.map(r => (
                  <option key={r.id} value={r.name}>{ROLE_LABELS[r.name] ?? r.name}</option>
                ))}
              </select>
            </label>
            <label className="ud-checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              פעיל
            </label>
            <label className="ud-form-full">
              סיסמה חדשה (השאר ריק לאי-שינוי)
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="• • • • • • • •" />
            </label>

            <h3 className="ud-section-title ud-form-full">פרטי חברה</h3>
            <label>
              שם חברה
              <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="שם החברה" />
            </label>
            <label className="ud-form-full">
              תיאור / פרטי חברה
              <textarea value={form.company_details} onChange={e => set('company_details', e.target.value)} rows={3} placeholder="תיאור החברה" />
            </label>
            <label className="ud-form-full">
              כתובת חברה
              <input value={form.company_address} onChange={e => set('company_address', e.target.value)} placeholder="כתובת מלאה" />
            </label>

            {saveError && <p className="form-error ud-form-full">{saveError}</p>}
            {saveSuccess && <p className="form-success ud-form-full">השינויים נשמרו בהצלחה</p>}
            <div className="form-actions ud-form-full">
              <button type="button" onClick={() => navigate('/settings/user-management')}>ביטול</button>
              <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'שומר...' : 'שמור שינויים'}
              </button>
            </div>
          </form>
        </section>

        {/* ─── Assigned Drones ─── */}
        <section className="ud-section panel">
          <div className="ud-drones-header">
            <h2>רחפנים משויכים</h2>
            <button className="btn-primary" onClick={() => setShowAssignDrone(v => !v)}>
              + שייך רחפן
            </button>
          </div>

          {showAssignDrone && (
            <div className="ud-assign-row">
              <select value={selectedDroneId} onChange={e => setSelectedDroneId(e.target.value)}>
                <option value="">— בחר רחפן —</option>
                {availableDrones.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.drone_id}{d.tail_number ? ` (זנב: ${d.tail_number})` : ''}
                  </option>
                ))}
              </select>
              <button
                className="btn-primary"
                disabled={!selectedDroneId || assignDroneMutation.isPending}
                onClick={() => selectedDroneId && assignDroneMutation.mutate(selectedDroneId)}
              >
                {assignDroneMutation.isPending ? 'משייך...' : 'שייך'}
              </button>
              <button onClick={() => setShowAssignDrone(false)}>ביטול</button>
            </div>
          )}

          {user.assigned_drones.length === 0 ? (
            <p className="ud-empty">אין רחפנים משויכים למשתמש זה.</p>
          ) : (
            <div className="ud-drones-list">
              {user.assigned_drones.map(d => (
                <div key={d.id} className="ud-drone-card">
                  <Link to={`/drones/${d.id}`} className="ud-drone-link">
                    <div className="ud-drone-id">{d.drone_id}</div>
                    {d.tail_number && <div className="ud-drone-tail">זנב: {d.tail_number}</div>}
                    <span className={`status status-${d.status}`}>{d.status}</span>
                  </Link>
                  <button
                    className="ud-unassign-btn"
                    onClick={() => unassignDroneMutation.mutate(d.id)}
                    title="הסר שיוך"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
